/**
 * The All Sports Arena home. Brand values come from `ASA_COLOR` (asaBrand.ts); everything that
 * follows light / dark comes from the village surface.
 */
import styled from '@emotion/styled';
import { Box, ButtonBase } from '@mui/material';
import { ASA_COLOR } from '../../../Utils/asaBrand';
import { withAlpha } from '../../../Utils/dfcBrand';
import { DFC_VILLAGE_DESKTOP_MEDIA } from '../../../hooks/useDfcVillageDesktop';
import { SurfaceProp, focusRing, omit } from '../../../components/shared/dfcVillage/style';

/** Full-bleed photo with a navy wash: white text on it stays above 12:1 whatever the photo. */
export const HeroBleed = styled(Box, omit('image'))<{ image?: string }>`
  position: relative;
  display: flex;
  align-items: flex-end;
  min-height: 420px;
  padding: 32px 16px;
  color: ${ASA_COLOR.white};
  background-color: ${ASA_COLOR.navy};
  background-image: linear-gradient(
      180deg,
      ${withAlpha(ASA_COLOR.navy, 0.55)} 0%,
      ${withAlpha(ASA_COLOR.navy, 0.88)} 70%,
      ${ASA_COLOR.navy} 100%
    ),
    ${({ image }) => (image ? `url("${image}")` : 'none')};
  background-size: cover;
  background-position: center;

  ${DFC_VILLAGE_DESKTOP_MEDIA} {
    min-height: 560px;
    padding: 64px 40px;
  }
`;

export const HeroInner = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-width: 880px;
`;

export const HeroTitle = styled('h1')`
  && {
    margin: 0;
    font-size: 48px;
    line-height: 0.95;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: -0.02em;

    ${DFC_VILLAGE_DESKTOP_MEDIA} {
      font-size: 104px;
      letter-spacing: -0.03em;
    }
  }
`;

/** ASA's motif: the word in square brackets on a royal-blue band, white text (7.34:1). */
export const BracketWord = styled('span')`
  display: inline-block;
  margin-top: 6px;
  padding: 0 0.12em;
  background: ${ASA_COLOR.royalBlue};
  color: ${ASA_COLOR.white};
  white-space: nowrap;
`;

export const HeroIntro = styled('p')`
  && {
    margin: 0;
    font-size: 16px;
    line-height: 1.5;
    color: ${withAlpha(ASA_COLOR.white, 0.9)};

    ${DFC_VILLAGE_DESKTOP_MEDIA} {
      font-size: 18px;
    }
  }
`;

export const HeroActions = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

export const HeroAction = styled(ButtonBase, omit('tone'))<{ tone: 'blue' | 'white' }>`
  && {
    min-height: 48px;
    padding: 0 26px;
    border-radius: 999px;
    font: inherit;
    font-size: 15px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    background: ${({ tone }) => (tone === 'blue' ? ASA_COLOR.royalBlue : ASA_COLOR.white)};
    color: ${({ tone }) => (tone === 'blue' ? ASA_COLOR.white : ASA_COLOR.navy)};
    transition: transform 120ms ease;

    &:hover {
      transform: translateY(-1px);
    }
    &:focus-visible {
      outline: 2px solid ${ASA_COLOR.white};
      outline-offset: 3px;
    }
    @media (prefers-reduced-motion: reduce) {
      transition: none;
      &:hover {
        transform: none;
      }
    }
  }
`;

export const Sections = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 28px 16px 40px;

  ${DFC_VILLAGE_DESKTOP_MEDIA} {
    gap: 48px;
    padding: 48px 40px 64px;
  }
`;

/** "Coming up" beside the account panel from 960px; stacked below. */
export const SplitRow = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${DFC_VILLAGE_DESKTOP_MEDIA} {
    display: grid;
    grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
    align-items: start;
    gap: 32px;
  }
`;

export const SessionRail = styled(Box)`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(200px, 1fr);
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 4px;

  ${DFC_VILLAGE_DESKTOP_MEDIA} {
    grid-auto-flow: row;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    overflow: visible;
  }
`;

export const SessionCard = styled(ButtonBase, omit('surface', 'full'))<
  SurfaceProp & { full?: boolean }
>`
  && {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 14px 16px;
    border-radius: 16px;
    text-align: start;
    font: inherit;
    color: ${({ surface }) => surface.ink};
    background: ${({ surface }) => surface.surface};
    border: 1px solid ${({ surface }) => surface.border};
    border-left: 4px solid ${({ surface }) => surface.accentMark};
    opacity: ${({ full }) => (full ? 0.55 : 1)};
    transition: background-color 120ms ease;

    &:hover {
      background: ${({ surface, full }) => (full ? surface.surface : surface.soft)};
    }
    ${({ surface }) => focusRing(surface)}
  }
`;

export const SessionWhen = styled('span', omit('surface'))<SurfaceProp>`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ surface }) => surface.accentText};
`;

export const SessionTitle = styled('span')`
  font-size: 17px;
  font-weight: 800;
`;

export const SessionMeta = styled('span', omit('surface'))<SurfaceProp>`
  font-size: 12px;
  color: ${({ surface }) => surface.ink2};
`;

export const PanelCard = styled(Box, omit('surface'))<SurfaceProp>`
  padding: 24px 20px;
  border-radius: 20px;
  background: ${({ surface }) => surface.surface};
  border: 1px solid ${({ surface }) => surface.border};

  [data-testid='register-wrapper'] {
    max-width: none;
    margin: 0;
    padding: 0;
  }
`;

export const WelcomeTitle = styled('h2', omit('surface'))<SurfaceProp>`
  && {
    margin: 0 0 8px;
    font-size: 24px;
    font-weight: 800;
    text-transform: uppercase;
    color: ${({ surface }) => surface.ink};
  }
`;
